import { cloneElement, useId, useState, type ReactElement, type ReactNode } from 'react';
import { cx } from './util.ts';
import './Tooltip.css';

export interface TooltipProps {
  content: ReactNode;
  /** Un único elemento enfocable (botón, enlace); recibe aria-describedby. */
  children: ReactElement<{ 'aria-describedby'?: string }>;
  side?: 'top' | 'bottom';
}

/**
 * Ayuda breve que aparece al pasar el ratón o al enfocar y desaparece con Escape. Es una
 * descripción, no el nombre: el elemento debe tener ya su propio nombre accesible.
 */
export function Tooltip({ content, children, side = 'top' }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const describedBy = [children.props['aria-describedby'], id].filter(Boolean).join(' ');

  return (
    <span
      className="af-tooltip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      {cloneElement(children, { 'aria-describedby': describedBy })}
      <span
        role="tooltip"
        id={id}
        hidden={!open}
        className={cx('af-tooltip__bubble', `af-tooltip__bubble--${side}`)}
      >
        {content}
      </span>
    </span>
  );
}
